import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { ConfigurationWidgets } from '../../interfaces/mdr-dashboards-widgets.interface';
import { HttpClient } from '@angular/common/http';
import { distinctUntilChanged } from 'rxjs/operators';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { OrganizationData } from '../../../organizations/interfaces/OrganizationData';
import { isEqual } from 'lodash-es';
import { Subscription } from 'rxjs/internal/Subscription';
import { UserPermission } from '../../../auth/enums/user-permission.enum';
import { ExternalDataEndpointsInterface } from '../../interfaces/mdr-dashboards-drilldown.interface';
import { IdentityService } from '../../../auth/services/identity.service';
import { ThemeService } from '../../../../services/theme.service';

@Component({
    selector: 'app-mdr-dashboards-configuration',
    templateUrl: './mdr-dashboards-configuration.component.html',
    styleUrls: ['./mdr-dashboards-configuration.component.scss'],
    standalone: false,
})
export class MdrDashboardsConfigurationComponent implements OnInit, OnDestroy {
    public dataSource: ConfigurationWidgets;

    public organization: OrganizationData | null;
    public timeframe: TimeframeData;
    public timeframeSub: Subscription;
    public orgSubscription: Subscription;

    public UserPermission = UserPermission;
    public noIssuesOnboarding: number = -1;

    public constructor(
        public http: HttpClient,
        public timeframeService: TimeframeService,
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public identityService: IdentityService,
        public cdr: ChangeDetectorRef,
        public themeService: ThemeService,
    ) {}

    ngOnDestroy(): void {
        if (this.orgSubscription) {
            this.orgSubscription.unsubscribe();
        }
        if (this.timeframeSub) {
            this.timeframeSub.unsubscribe();
        }
        this.cdr.detach();
    }

    public ngOnInit() {
        this.timeframeSub = this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                this.timeframe = timeframe;
                this.updateDataSource(timeframe, this.organizationService.currentlySelectedOrganization());
            });
        this.orgSubscription = this.organizationService.selectedOrganization$().subscribe((val) => {
            this.noIssuesOnboarding = -1;
            this.organization = val;
            this.updateDataSource(this.timeframeService.currentlySelectedTimeframe(), val);
            this.dataSource.statusOfDeployment[0].data.subscribe((val) => {
                this.noIssuesOnboarding = val.data.data.filter((val: any) => val.issue).length;
                this.cdr.detectChanges();
            });
        });

        this.identityService.companyLoaded$.subscribe((isLoaded) => {
            if (isLoaded) {
                this.updateDataSource(
                    this.timeframeService.currentlySelectedTimeframe(),
                    this.organizationService.currentlySelectedOrganization(),
                );
            }
        });
    }

    /**
     * Use this function to update each widget when one of the selectors (organization and timeframe) are changed
     * @param timeframe - retrieve data for the selected timeframe
     * @param organization - retrieve data for the selected organization
     */
    public updateDataSource(timeframe: TimeframeData | null, organization: OrganizationData | null) {
        const parent = this.organizationService.currentlySelectedOrganizationHasChildren() ? 1 : 0;
        const org = organization || this.identityService.company;
        const isMsp =
            (org?.parent_is_msp || org?.details?.configuration.ui_settings.is_msp) &&
            !org?.details?.configuration.features.enableAggregateLicense;
        const endpointsMonitoringStatusMapping: ExternalDataEndpointsInterface[] = [
            {
                name: 'Monitored by MDR',
                endpoint:
                    '/v2/reports/download-endpoints-monitored?format=csv&company_id=' +
                    org?.id +
                    '&time=' +
                    timeframe?.name +
                    '&parent=' +
                    parent,
                filename: 'monitored-by-mdr',
            },
            {
                name: 'Telemetry policy issues',
                endpoint:
                    '/v2/reports/download-endpoints-telemetry-issues?format=csv&company_id=' +
                    org?.id +
                    '&time=' +
                    timeframe?.name +
                    '&parent=' +
                    parent,
                filename: 'telemetry-issues',
            },
            {
                name: 'EDR issues',
                endpoint:
                    '/v2/reports/download-endpoints-edr-issues?format=csv&company_id=' +
                    org?.id +
                    '&time=' +
                    timeframe?.name +
                    '&parent=' +
                    parent,
                filename: 'edr-issues',
            },
            {
                name: 'License issues',
                endpoint:
                    '/v2/reports/download-endpoints-license-issues?format=csv&company_id=' +
                    org?.id +
                    '&time=' +
                    timeframe?.name +
                    '&parent=' +
                    parent,
                filename: 'license-issues',
            },
            {
                name: 'No telemetry',
                endpoint:
                    '/v2/reports/download-endpoints-other-issues?format=csv&company_id=' +
                    org?.id +
                    '&time=' +
                    timeframe?.name +
                    '&parent=' +
                    parent,
                filename: 'other-issues',
            },
        ];

        this.dataSource = {
            indicatorOptions: [
                {
                    title: isMsp ? 'Total GravityZone Seats' : 'Total EDR licenses',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/total-licenses', organization),
                    delta: '',
                    id: 'total_licenses_indicator',
                },
                {
                    title: isMsp ? 'MDR Usage' : 'Licensed endpoints',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/licensed-endpoints', organization),
                    value: 150,
                    delta: '',
                    id: 'licensed_endpoints_indicator',
                },
                {
                    title: isMsp ? 'GravityZone Active Endpoints' : 'Active endpoints',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/active-endpoints', organization),
                    value: 85,
                    delta: '',
                    id: 'active_endpoints_indicator',
                },
                {
                    title: isMsp ? 'MDR Monitored Endpoints' : 'Monitored Endpoints',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/monitored-endpoints', organization),
                    value: 241,
                    delta: '',
                    id: 'monitored_endpoints_indicator',
                },
            ],
            endpointsByOS: {
                id: 'endpoints_by_os_chart',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/endpoints-os', organization),
                noDataLabels: ['Windows', 'OSX', 'Linux'],
                noDataSeries: [110, 0, 0],
                colors: ['#295CA5', '#6771DC', '#67B7DC'],
                title: 'Monitored Endpoints by OS',
                approximate: false,
            },
            alertsByOS: {
                id: 'alerts_by_os_chart',
                noDataSeries: [
                    {
                        name: 'Linux',
                        data: [],
                    },
                    {
                        name: 'OSX',
                        data: [],
                    },
                    {
                        name: 'Windows',
                        data: [],
                    },
                ],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/alerts-os', organization),
                colors: ['#67B7DC', '#5B64C2', '#AD6DE8'],
                categories: ['Ransomware', 'Exploit', 'Password Stealer', 'Malware', 'PUA', 'Defined by user', 'Other'],
                title: 'Alerts by OS',
                approximate: true,
            },
            topEndpointsByAlerts: {
                id: 'top_endpoints_by_alerts_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-endpoints-alerts', organization),
                columns: ['logo', 'name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                no_rows: 10,
                title: 'Top endpoints by alerts',
            },
            endpointsByOsTrend: {
                id: 'endpoints_by_os_trend',
                noDataLegend: [
                    {
                        name: 'Windows',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'OSX',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Linux',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                ],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/endpoints-os-trend', organization),
                colors: ['#67B7DC', '#5B64C2', '#AD6DE8'],
                strokeColors: ['#67B7DC', '#5B64C2', '#AD6DE8'],
                title: 'Endpoints by OS trend',
                columns: [],
                approximate: true,
            },
            statusOfDeployment: [
                {
                    id: 'onboarding_status_chart',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/onboarding-status', organization),
                    noDataLabels: ['None'],
                    noDataSeries: [0],
                    colors: ['#FFD600', '#FF883F', '#E90A0A'],
                    title: 'Status of deployment',
                    approximate: true,
                    mode: 'progress',
                    label: 'Onboarding',
                    delta: false,
                    delimitation: true,
                },
                {
                    id: 'monitoring_status_chart',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/monitoring-status', organization, true),
                    noDataLabels: ['None'],
                    noDataSeries: [0],
                    colors: ['#295CA5'],
                    title: 'Status of deployment',
                    approximate: true,
                    mode: 'percent',
                    label: 'Monitored',
                    delta: true,
                    delimitation: false,
                },
            ],
            endpointMonitoringStatus: {
                id: 'active_endpoints_status_chart',
                url: '/monitoring-status',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/endpoint-monitoring-status', organization),
                noDataLabels: ['No telemetry', 'Telemetry policy issues', 'EDR issues', 'License issues', 'Monitored by MDR'],
                noDataSeries: [110, 0, 0, 0, 0],
                colors: ['#FFB74D', '#EF5350', '#922227', '#F02A42', '#A2D179'],
                title: 'Gravityzone Active Endpoint Status',
                approximate: false,
                no_rows: 5,
                columns: ['name', 'value'],
                drilldown: {
                    show: true,
                    hover_color: 'primary',
                    hover_icon: 'ds-icon-download',
                    allowDownloadData: true,
                    externalData: {
                        endpoints: endpointsMonitoringStatusMapping,
                        filename: 'endpoint-data',
                        type: 'text/csv',
                    },
                },
            },
        };
        this.cdr.detectChanges();
    }
}
